/**
 * Correction Embed Entry Point
 *
 * The embed is a pure UI component. It does NOT authenticate and does
 * NOT know any PaperScorer URLs — the host page is the source of truth
 * for both. We offer two ways to mount:
 *
 * 1. **Programmatic mount** (full control). Host fetches sheet data
 *    itself and hands it to the embed along with a submit callback.
 *
 * 2. **Auto-mount** (drop-in HTML, no JS code). Host marks a container
 *    with `data-ps-correction-embed` and supplies fetch / submit URLs
 *    on the same element via data attributes. The embed scans the DOM
 *    on `DOMContentLoaded`, fetches the sheet, and posts corrections
 *    back. The host's backend is still the auth boundary — those URLs
 *    point at the host's own server, not at PaperScorer directly, so
 *    your `Consumer-Key` never enters the browser.
 *
 * Programmatic usage:
 *
 *   <div id="my-correction"></div>
 *
 *   <script src="/embed/correction.js"></script>
 *   <script>
 *     const data = await fetch('/my-backend/correction-data?task_id=...').then(r => r.json());
 *
 *     window.PSCorrectionEmbed.mount(document.getElementById('my-correction'), {
 *       data,
 *       async onSubmit(corrected) {
 *         const res = await fetch('/my-backend/submit-correction', {
 *           method: 'POST',
 *           headers: { 'Content-Type': 'application/json' },
 *           body: JSON.stringify({ task_id: '...', form_id: '...', ...corrected }),
 *         });
 *         return res.json();
 *       },
 *     });
 *   </script>
 *
 * Auto-mount usage:
 *
 *   <div data-ps-correction-embed
 *        data-fetch-url="/my-backend/correction-data?task_id=619&form_id=F-100"
 *        data-submit-url="/my-backend/submit-correction"
 *        data-task-id="619"
 *        data-form-id="F-100"></div>
 *
 *   <script src="/embed/correction.js"></script>
 *
 * `data` may be a Promise (programmatic) — useful when you want to
 * mount the embed (and show its loading state) before the fetch
 * resolves.
 *
 * Optional config (both modes):
 *
 *   showStudentIdInput / data-show-student-id-input:
 *     'auto' (default) | true | false
 *     'auto' hides the input when the sheet is pre-filled
 *     (data.template_id === 0). Override to force show/hide.
 *
 *   showQuestions / data-show-questions:
 *     'auto' (default) | true | false
 *     'auto' hides the question grid when the bubbled-in student ID
 *     wasn't read (engine returns 0/empty). The engine can't produce
 *     question data until a sheet has a resolved student, so the
 *     two-step UX is: fix the ID first, resubmit to re-process, then
 *     come back to correct bubbles. Override to force show/hide.
 */

import { createApp } from 'vue';
import CorrectionEmbed from './CorrectionEmbed.vue';

const AUTO_MOUNT_SELECTOR = '[data-ps-correction-embed]';

function mount(el, options) {
    if (!el || el.dataset.psCorrectionMounted === '1') return null;
    if (!options || typeof options !== 'object') {
        throw new Error('PSCorrectionEmbed.mount: options { data, onSubmit } are required.');
    }
    if (typeof options.onSubmit !== 'function') {
        throw new Error('PSCorrectionEmbed.mount: options.onSubmit must be a function.');
    }
    if (options.data === undefined || options.data === null) {
        throw new Error('PSCorrectionEmbed.mount: options.data is required (object or Promise).');
    }

    el.dataset.psCorrectionMounted = '1';

    const embedProps = {
        data: options.data,
        onSubmit: options.onSubmit,
    };
    if (options.showStudentIdInput !== undefined) {
        embedProps.showStudentIdInput = options.showStudentIdInput;
    }
    if (options.showQuestions !== undefined) {
        embedProps.showQuestions = options.showQuestions;
    }

    const app = createApp(CorrectionEmbed, embedProps);
    app.mount(el);

    return app;
}

/**
 * Parse a tri-state data attribute. 'true'/'false' map to booleans;
 * anything else (including absent / 'auto') leaves the embed default.
 */
function parseShowFlag(raw) {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return undefined;
}

/**
 * Pure helper: build the `{ data, onSubmit, ... }` mount options from
 * an element's data attributes. Returns null when the required
 * `data-fetch-url` / `data-submit-url` attributes are missing.
 *
 * Extracted so it can be unit-tested with a stubbed fetch — autoMount
 * is the thin wrapper that pairs this with the actual `mount` call.
 *
 * @param {HTMLElement} el
 * @param {typeof fetch} [fetchImpl] — defaults to the global `fetch`.
 */
function buildAutoMountOptions(el, fetchImpl) {
    if (!el) return null;
    const f = fetchImpl || (typeof fetch === 'function' ? fetch : null);
    if (!f) return null;

    const fetchUrl = el.dataset.fetchUrl;
    const submitUrl = el.dataset.submitUrl;
    if (!fetchUrl || !submitUrl) return null;

    const taskId = el.dataset.taskId || '';
    const formId = el.dataset.formId || '';

    // Build the fetch as a Promise so the embed's loading state covers
    // the round-trip. Accept either the standard envelope shape
    // ({ success, data, ... }) or a raw sheet object — same flexibility
    // the embed already grants to programmatic callers.
    const dataPromise = (async () => {
        const res = await f(fetchUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
            throw new Error(`Failed to load sheet (HTTP ${res.status}).`);
        }
        const json = await res.json();
        return json && typeof json.data === 'object' && json.data !== null
            ? json.data
            : json;
    })();

    const onSubmit = async (corrected) => {
        // Always include the embed's corrected fields. Fold in task_id
        // and form_id from data attributes when present — the partner's
        // submit endpoint usually needs them to route to the right
        // /api/submit-correction-sheet call.
        const body = { ...corrected };
        if (taskId) body.task_id = taskId;
        if (formId) body.form_id = formId;

        const res = await f(submitUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify(body),
        });

        // Some backends return a non-JSON body on error pages; fall back
        // to an empty object so we still surface a useful message.
        let result = {};
        try {
            result = await res.json();
        } catch (e) {
            result = {};
        }

        if (!res.ok || result.success === false) {
            throw new Error(result.message || `Submit failed (HTTP ${res.status}).`);
        }
        return result;
    };

    const options = { data: dataPromise, onSubmit };
    const sii = parseShowFlag(el.dataset.showStudentIdInput);
    if (sii !== undefined) options.showStudentIdInput = sii;
    const sq = parseShowFlag(el.dataset.showQuestions);
    if (sq !== undefined) options.showQuestions = sq;

    return options;
}

/**
 * Mount a single element using its data attributes for configuration.
 * Skips silently (with a console warning) if required attributes are
 * missing, so a partially-configured drop-in doesn't break the page.
 *
 * @param {HTMLElement} el
 * @param {{ mountFn?: typeof mount, fetchImpl?: typeof fetch }} [opts]
 */
function autoMount(el, opts) {
    if (!el || el.dataset.psCorrectionMounted === '1') return null;

    const options = buildAutoMountOptions(el, opts && opts.fetchImpl);
    if (!options) {
        // Drop-in mode is opt-in via the marker attribute, so a missing
        // URL is a misconfiguration we want to surface to the partner —
        // warn, don't throw (one broken instance shouldn't bring down
        // the rest of the page).
        if (typeof console !== 'undefined' && typeof console.warn === 'function') {
            console.warn(
                '[PSCorrectionEmbed] auto-mount requires both data-fetch-url and data-submit-url; skipping element:',
                el
            );
        }
        return null;
    }

    const mountFn = (opts && opts.mountFn) || mount;
    return mountFn(el, options);
}

/**
 * Scan the given root for `[data-ps-correction-embed]` elements and
 * auto-mount each one. Safe to call multiple times — already-mounted
 * elements skip themselves via the dataset guard in `mount`. Returns
 * the array of mounted Vue app instances (skipped elements are
 * filtered out).
 *
 * @param {ParentNode} [root] — defaults to `document`.
 * @param {{ mountFn?: typeof mount, fetchImpl?: typeof fetch }} [opts]
 */
function mountAll(root, opts) {
    const scope = root || (typeof document !== 'undefined' ? document : null);
    if (!scope || typeof scope.querySelectorAll !== 'function') return [];
    return Array.from(scope.querySelectorAll(AUTO_MOUNT_SELECTOR))
        .map((el) => autoMount(el, opts))
        .filter((v) => v !== null);
}

if (typeof window !== 'undefined') {
    window.PSCorrectionEmbed = window.PSCorrectionEmbed || {};
    window.PSCorrectionEmbed.mount = mount;
    window.PSCorrectionEmbed.mountAll = mountAll;
    window.PSCorrectionEmbed.autoMount = autoMount;

    // Auto-init: scan once the DOM is parseable. Already-mounted
    // elements (e.g. via a manual `mount()` call earlier in the page)
    // skip themselves, so re-running is harmless.
    const autoInit = () => mountAll(document);
    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', autoInit, { once: true });
        } else {
            // DOM already parsed — defer to a microtask so any inline
            // script registering a programmatic mount earlier on the
            // same tick wins the dataset guard race.
            queueMicrotask(autoInit);
        }
    }
}

export { mount, autoMount, mountAll, buildAutoMountOptions };
